import { type FC, useEffect, useState, useCallback, useRef } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId } from 'dibs-cookie-jar';
import { OverlaySpinner } from 'dibs-elements/exports/OverlaySpinner';
import RelatedPhotosRendererSuspense from 'dibs-interiors-photos/exports/RelatedPhotosRendererSuspense';
import { PhotoRootQuery } from './PhotoRootQuery';
import PhotoContent from './content/PhotoContent';
import MobilePhotoContent from './content/MobilePhotoContent';
import { trackPageView } from '../shared/helpers/tracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { useBuildPhotoTitle } from './hooks/useBuildPhotoTitle';
import { usePhotoLookupHistory } from './hooks/usePhotoLookupHistory';
import { useFetchDesignProjectPhotos } from './hooks/useFetchDesignProjectPhotos';
import { useAutosavePhoto } from './hooks/useAutosavePhoto';
import { useServerVars } from '../shared/ServerVarsContext';
import { useTrackTradePhotoPageview } from 'dibs-interiors-photos/exports/interiorsPhotosTracking';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import styles from './InteriorPhotoWrapper.scss';

import { type Photo } from 'dibs-interiors-photos/exports/types';
import { type InteriorPhotoWrapper_viewer$data } from './__generated__/InteriorPhotoWrapper_viewer.graphql';

const getUserId = (): string => getBuyerId(document.cookie);

export type TradePhotoNode = NonNullable<
    ArrayElement<NonNullable<InteriorPhotoWrapper_viewer$data['tradePhotosQuery']>['edges']>
>['node'];

type Props = {
    viewer: InteriorPhotoWrapper_viewer$data;
    relay: RelayRefetchProp;
    isModal?: boolean;
    initialLookupUrl: string;
    prefetchedPhotos?: Photo[];
    onCloseModal?: () => void;
};

const InteriorPhotoWrapper: FC<Props> = props => {
    const { isModal = false, viewer, initialLookupUrl, relay, onCloseModal } = props;
    const [lookupUrl, setLookupUrl] = useState(initialLookupUrl);
    const [nextLookupUrl, setNextLookupUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userId, setUserId] = useState('');

    const prefetchedPhotos = (props.prefetchedPhotos || []).filter(filterNulls);
    const hasPrefetchedPhotos = prefetchedPhotos.length > 0;
    const [fetchedPhotos, setFetchedPhotos] = useState<Photo[]>(prefetchedPhotos);
    const photoEdges = viewer?.tradePhotosQuery?.edges;

    const findPhotoNodeByUrl = useCallback(
        (url: string): TradePhotoNode | undefined =>
            photoEdges?.find(edge => edge?.node?.photo?.url === url)?.node,
        [photoEdges]
    );

    const photoNode = findPhotoNodeByUrl(lookupUrl) || null;
    const tradePhoto = photoNode?.photo || null;
    const firstPhotoNode = photoEdges?.[0]?.node;
    const firstPhotoUrl = firstPhotoNode?.photo?.url || '';
    const projectRootId = firstPhotoNode?.projectRootId || '';

    const photoId = tradePhoto?.serviceId || '';
    const photoLikeId = tradePhoto?.photoLike?.photoLikeId || '';
    const photoNodeId = tradePhoto?.id || '';

    const userIdRef = useRef('');

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        const buyerId = getUserId();
        setUserId(buyerId);

        addLoginHandlers(() => {
            const _userId = getUserId();
            setUserId(_userId);
            relay.refetch(fragmentVars => ({
                ...fragmentVars,
                userId: _userId,
                hasUserId: !!_userId,
            }));
        });
    }, [relay]);

    useEffect(() => {
        trackPageView();
    }, []);

    const handlePageChange = useCallback(
        (nextUrl: string): void => {
            const photoLoaded = !!findPhotoNodeByUrl(nextUrl);
            if (photoLoaded) {
                setLookupUrl(nextUrl);
            } else {
                setIsLoading(true);
                setNextLookupUrl(nextUrl);
            }
        },
        [findPhotoNodeByUrl]
    );

    const photoTitle = useBuildPhotoTitle({
        firmName: photoNode?.firmName,
        photoName: photoNode?.photo?.name,
    });

    const updateHistory = useCallback(
        (path: string, shouldReplace?: boolean) => {
            const fullPath = handleLocaleUrl(
                path + window.location.search,
                GLOBAL_CLIENT_ONLY_LOCALE
            );
            if (shouldReplace) {
                window.history.replaceState(fullPath, photoTitle, fullPath);
            } else {
                window.history.pushState(fullPath, photoTitle, fullPath);
            }
        },
        [photoTitle]
    );

    usePhotoLookupHistory({
        updateHistory,
        isModal,
        photoUrl: initialLookupUrl || firstPhotoUrl || '',
        findPhotoNodeByUrl,
        handlePageChange,
    });

    const isLoadingRef = useRef(false);
    const nextLookupUrlRef = useRef('');
    useEffect(() => {
        isLoadingRef.current = isLoading;
        nextLookupUrlRef.current = nextLookupUrl;
    });

    const [hasFetchedPhotoLikes, setHasFetchedPhotoLikes] = useState(false);

    useTrackTradePhotoPageview(tradePhoto);

    useAutosavePhoto({
        photoId,
        photoLikeId,
        photoNodeId,
        hasFetchedPhotoLikes,
    });

    const onFetchComplete = useCallback(
        (projectPhotos: Photo[]): void => {
            const urlLabels = projectPhotos.map(({ url }) => url);
            relay.refetch(
                { urlLabels, userId: userIdRef.current, hasUserId: !!userIdRef.current },
                null,
                error => {
                    if (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                    setFetchedPhotos(projectPhotos);
                    setHasFetchedPhotoLikes(true);

                    if (isLoadingRef.current && nextLookupUrlRef.current) {
                        setIsLoading(false);
                        setLookupUrl(nextLookupUrlRef.current);
                        setNextLookupUrl('');
                    }
                }
            );
        },
        [relay]
    );

    useFetchDesignProjectPhotos({
        projectRootId,
        relay,
        hasPrefetchedPhotos,
        onFetchComplete,
    });

    const onPageChange = useCallback(
        (nextPath: string) => {
            handlePageChange(nextPath);
            updateHistory(nextPath);
        },
        [handlePageChange, updateHistory]
    );

    const contentProps = {
        onPageChange,
        photoNode,
        photos: fetchedPhotos,
        isModal,
        onCloseModal,
    };

    const { isDesktop, isMobile } = useServerVars();
    const isClient = useClientState();

    const styleTag = tradePhoto?.styles?.[0];
    const roomTag = tradePhoto?.room;

    return (
        <div className={styles.wrapper}>
            {isDesktop ? (
                <PhotoContent {...contentProps} />
            ) : (
                <MobilePhotoContent {...contentProps} />
            )}
            {isClient && (
                <RelatedPhotosRendererSuspense
                    photoId={photoId}
                    isMobile={isMobile}
                    styleTag={styleTag}
                    roomTag={roomTag}
                    userId={userId}
                />
            )}
            <OverlaySpinner isOpen={isLoading} />
        </div>
    );
};

export default createRefetchContainer(
    InteriorPhotoWrapper,
    {
        viewer: graphql`
            fragment InteriorPhotoWrapper_viewer on Viewer {
                tradePhotosQuery(urlLabels: $urlLabels) {
                    edges {
                        node {
                            ...PhotoContent_photoNode
                            ...MobilePhotoContent_photoNode
                            projectId
                            projectRootId
                            firmName
                            photo {
                                ...interiorsPhotosTracking_tradePhoto
                                id
                                name
                                url
                                serviceId
                                styles {
                                    name
                                    value
                                    urlLabel
                                }
                                room {
                                    value
                                    name
                                    urlLabel
                                }
                                photoLike {
                                    photoLikeId(userId: $userId) @include(if: $hasUserId)
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    PhotoRootQuery
);
